import type { SkillView } from '@/core/types';
import type { AppState } from '@/core/state';
import { catalog } from '@/data/catalog';
import { clock } from '@/core/util/misc';
import { computeEligibility, skillRelevance, type EligibilityMap } from './eligibility';
import { deriveView } from './estimates';

/**
 * Memoised derived data. Views combine the evidence-based estimate with the
 * curriculum relevance — relevance never changes the estimate itself.
 */

let eligCache: { key: string; value: EligibilityMap } | null = null;

export function eligibilityOf(state: AppState): EligibilityMap {
  const p = state.profile;
  const key = p ? JSON.stringify([p.education, p.subjects]) : 'none';
  if (eligCache && eligCache.key === key) return eligCache.value;
  const value = p ? computeEligibility(p) : computeEligibility({ education: { stage: 'G10', pathway: 'compulsory', curriculumVersion: 'LK20', subjectChoices: {} }, subjects: [] });
  eligCache = { key, value };
  return value;
}

let viewCache: { estimates: AppState['estimates']; elig: EligibilityMap; bucket: number; value: Record<string, SkillView> } | null = null;

export function viewsOf(state: AppState, now = clock.now()): Record<string, SkillView> {
  const elig = eligibilityOf(state);
  const bucket = Math.floor(now / 60_000);
  if (viewCache && viewCache.estimates === state.estimates && viewCache.elig === elig && viewCache.bucket === bucket) return viewCache.value;
  const value: Record<string, SkillView> = {};
  for (const s of catalog.skills) {
    value[s.id] = deriveView(s, state.estimates[s.id], skillRelevance(s, elig[s.subjectId]), now);
  }
  viewCache = { estimates: state.estimates, elig, bucket, value };
  return value;
}

export function viewOf(state: AppState, skillId: string, now = clock.now()): SkillView | undefined {
  return viewsOf(state, now)[skillId];
}

export function isRelevant(v: SkillView) {
  return v.relevance === 'core' || v.relevance === 'foundation' || v.relevance === 'extension';
}
